/**
 * PortalSetup.js
 * First-time access page with token validation, email confirmation, and password creation
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, Form, Button, Alert, Spinner, Container, Row, Col, InputGroup } from 'react-bootstrap';
import { portalService, authService } from '../../services/api';
import EmailWarningModal from '../Common/EmailWarningModal';
import { checkEmailsForWarnings } from '../../utils/emailValidation';
import './Portal.css';

const PortalSetup = () => {
  const { token } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState('');
  const [athleteData, setAthleteData] = useState(null);

  const [formData, setFormData] = useState({
    loginEmail: '',
    password: '',
    confirmPassword: ''
  });
  const [existingParent, setExistingParent] = useState(null);
  const [existingChild, setExistingChild] = useState(null);
  const [requiresParentRegistration, setRequiresParentRegistration] = useState(false);
  const [minorEmailConflictName, setMinorEmailConflictName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState('');

  // Forgot password inline panel
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmailSending, setResetEmailSending] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);

  // Email validation warning modal state
  const [emailWarningState, setEmailWarningState] = useState({
    warnings: [],
    onIgnore: null,
  });

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setTokenError('Token non fornito nel link di accesso.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const result = await portalService.validateToken(token);

        if (result.success && result.data?.valid) {
          const data = result.data;
          setAthleteData(data.athlete);
          setTokenValid(true);

          if (data.existingParentAccount?.exists) {
            setExistingParent(data.existingParentAccount);
          }
          if (data.existingChildAccount?.exists) {
            setExistingChild(data.existingChildAccount);
          }
          if (data.requiresParentRegistration) {
            setRequiresParentRegistration(true);
            setMinorEmailConflictName(data.minorEmailConflictName || '');
          }

          // If already set up, automatically log in with token
          if (data.isAlreadySetup) {
            const loginResult = await portalService.tokenLogin(token);
            if (loginResult.success) {
              navigate('/portal/profile');
              window.location.reload();
              return;
            }
          }

          // Pre-fill default login email
          setFormData(prev => ({
            ...prev,
            loginEmail: data.existingParentAccount?.email || data.existingChildAccount?.email || data.athlete?.defaultLoginEmail || data.athlete?.email || ''
          }));
        } else {
          setTokenValid(false);
          setTokenError(result.message || 'Link di accesso non valido o scaduto.');
        }
      } catch (err) {
        console.error('Error validating token:', err);
        setTokenValid(false);
        setTokenError('Errore durante la verifica del link di accesso.');
      } finally {
        setLoading(false);
      }
    };

    validateToken();
  }, [token, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formError) setFormError('');
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    const email = formData.loginEmail;
    if (!email) return;

    // Build the returnUrl so the reset link in the email brings the user back here
    const returnUrl = `/portal/setup/${token}`;

    try {
      setResetEmailSending(true);
      await authService.requestPasswordReset(email, returnUrl);
      // Always show success to avoid user enumeration
      setResetEmailSent(true);
    } catch (err) {
      console.error('Error requesting password reset:', err);
      setResetEmailSent(true); // Still show success to avoid enumeration
    } finally {
      setResetEmailSending(false);
    }
  };


  const handleSubmit = async (e, bypassEmailWarning = false) => {
    if (e && e.preventDefault) e.preventDefault();

    if (!formData.password) {
      setFormError('La password è obbligatoria');
      return;
    }

    if (!existingParent?.exists) {
      if (!formData.loginEmail.trim()) {
        setFormError('L\'email di login è obbligatoria');
        return;
      }

      if (formData.password.length < 6) {
        setFormError('La password deve essere di almeno 6 caratteri');
        return;
      }

      if (formData.password !== formData.confirmPassword) {
        setFormError('Le password non coincidono');
        return;
      }

      if (!bypassEmailWarning) {
        const warnings = checkEmailsForWarnings([
          {
            label: 'Email di Accesso (Login)',
            value: formData.loginEmail.trim(),
            fieldId: 'setup-login-email',
          },
        ]);
        if (warnings.length > 0) {
          setEmailWarningState({
            warnings,
            onIgnore: () => handleSubmit(null, true),
          });
          return;
        }
      }
    }

    try {
      setSubmitLoading(true);
      setFormError('');

      const result = existingParent?.exists
        ? await portalService.linkParent(token, formData.password)
        : await portalService.setup(token, formData.loginEmail.trim(), formData.password);

      if (result.success) {
        navigate('/portal/profile');
        window.location.reload();
      } else {
        setFormError(result.message || 'Errore durante la configurazione dell\'account');
        if (result.requiresParentRegistration || result.message?.includes('Questa email appartiene già a un profilo minorenne')) {
          setRequiresParentRegistration(true);
        }
      }
    } catch (err) {
      console.error('Error setting up portal account:', err);
      setFormError('Errore di connessione al server');
    } finally {
      setSubmitLoading(false);
    }
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <div className="py-5">
          <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
          <h4 className="mt-3 text-muted">Verifica link di accesso in corso...</h4>
        </div>
      </Container>
    );
  }

  if (!tokenValid) {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={8} lg={6}>
            <Card className="shadow border-0 text-center p-4">
              <Card.Body>
                <div className="text-danger mb-3">
                  <i className="fas fa-exclamation-circle fa-4x"></i>
                </div>
                <h3 className="mb-3">Link non valido o scaduto</h3>
                <Alert variant="danger" className="text-start">
                  {tokenError}
                </Alert>
                <p className="text-muted">
                  I link di accesso hanno una validità temporale limitata. Se il link è scaduto o è già stato utilizzato, contatta la segreteria o l'amministratore per richiedere un nuovo link.
                </p>
                <div className="mt-4">
                  <Link to="/login" className="btn btn-primary">
                    <i className="fas fa-sign-in-alt me-2"></i>
                    Vai alla pagina di Login
                  </Link>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <Row className="justify-content-center">
        <Col md={9} lg={7} xl={6}>
          <Card className="shadow-lg border-0">
            <Card.Header className="bg-primary text-white text-center py-4">
              <h3 className="mb-1">
                <i className="fas fa-running me-2"></i>
                Benvenuto, {athleteData?.firstName}!
              </h3>
              <p className="mb-0 text-white-50">Configurazione accesso al Portale Atleti</p>
            </Card.Header>
            <Card.Body className="p-4 p-md-5">
              {requiresParentRegistration ? (
                <Alert variant="warning" className="d-flex align-items-start mb-4" data-testid="requires-parent-alert">
                  <i className="fas fa-exclamation-triangle fs-4 me-3 text-warning mt-1"></i>
                  <div>
                    <h6 className="alert-heading mb-1 fw-bold">Registrazione Profilo Genitore Richiesta</h6>
                    <p className="mb-2 small">
                      Questa email appartiene già a un profilo minorenne{minorEmailConflictName ? ` (${minorEmailConflictName})` : ''}. È necessario attivare prima l'account genitore per gestire più atleti con la stessa email.
                    </p>
                    <Link
                      to={`/portal/register-parent/${token}`}
                      className="btn btn-warning btn-sm fw-bold text-dark"
                      data-testid="register-parent-link-banner"
                    >
                      <i className="fas fa-user-plus me-1"></i>
                      Clicca qui per registrare il profilo genitore
                    </Link>
                  </div>
                </Alert>
              ) : existingParent?.exists ? (
                <Alert variant="info" className="d-flex align-items-start mb-4" data-testid="existing-parent-alert">
                  <i className="fas fa-info-circle fs-4 me-3 text-info mt-1"></i>
                  <div>
                    <h6 className="alert-heading mb-1 fw-bold">Bentornato/a {existingParent.name || ''}!</h6>
                    <p className="mb-1 small">
                      L'indirizzo email <strong>{existingParent.email}</strong> è già registrato come account su Sportus.
                    </p>
                    <p className="mb-0 small">
                      Inserisci la password del tuo account per collegare la scheda di <strong>{athleteData?.firstName} {athleteData?.lastName}</strong> e accedere al portale.
                    </p>
                  </div>
                </Alert>
              ) : existingChild?.exists ? (
                <Alert variant="info" className="d-flex align-items-start mb-4" data-testid="existing-child-alert">
                  <i className="fas fa-info-circle fs-4 me-3 text-info mt-1"></i>
                  <div>
                    <h6 className="alert-heading mb-1 fw-bold">Configurazione Account Genitore</h6>
                    <p className="mb-1 small">
                      L'indirizzo email <strong>{existingChild.email}</strong> è attualmente assegnato al profilo di tuo figlio <strong>{existingChild.name}</strong>.
                    </p>
                    <p className="mb-0 small">
                      Inserisci la password del profilo di tuo figlio per completare l'attivazione del tuo account genitore e migrare le credenziali.
                    </p>
                  </div>
                </Alert>
              ) : (
                <p className="text-muted mb-4">
                  Per completare l'attivazione del tuo account, conferma l'email che userai per accedere e crea una nuova password sicura.
                </p>
              )}

              {formError && (
                <Alert variant="danger" onClose={() => setFormError('')} dismissible data-testid="setup-error-alert">
                  <div>{formError}</div>
                  {(requiresParentRegistration || formError.includes('Questa email appartiene già a un profilo minorenne')) && (
                    <div className="mt-2 pt-2 border-top border-danger-subtle">
                      <Link
                        to={`/portal/register-parent/${token}`}
                        className="alert-link fw-bold"
                        data-testid="register-parent-link-error"
                      >
                        <i className="fas fa-arrow-right me-1"></i>
                        Clicca qui per registrare il profilo genitore
                      </Link>
                    </div>
                  )}
                </Alert>
              )}

              <Form onSubmit={handleSubmit}>
                {existingParent?.exists ? (
                  <>
                    <Form.Group className="mb-3" controlId="parent-login-email">
                      <Form.Label className="fw-bold">Account Genitore</Form.Label>
                      <Form.Control
                        type="email"
                        name="loginEmail"
                        value={formData.loginEmail}
                        disabled
                      />
                      <Form.Text className="text-muted">
                        Account a cui verrà collegata la scheda atleta del minore.
                      </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-4" controlId="parent-password">
                      <Form.Label className="fw-bold">Password del tuo account genitore *</Form.Label>
                      <InputGroup>
                        <Form.Control
                          type={showPassword ? 'text' : 'password'}
                          name="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          placeholder="Inserisci la tua password"
                          required
                          disabled={submitLoading}
                        />
                        <Button
                          variant="outline-secondary"
                          onClick={() => setShowPassword(!showPassword)}
                          type="button"
                        >
                          <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                        </Button>
                      </InputGroup>
                    </Form.Group>

                    {/* Forgot password inline panel */}
                    <div className="mb-4" data-testid="forgot-password-section">
                      {!showForgotPassword ? (
                        <div className="text-end">
                          <Button
                            variant="link"
                            className="p-0 text-decoration-none small"
                            onClick={() => { setShowForgotPassword(true); setResetEmailSent(false); }}
                            type="button"
                            data-testid="forgot-password-link"
                          >
                            Password dimenticata?
                          </Button>
                        </div>
                      ) : (
                        <div className="border rounded p-3 bg-light" data-testid="forgot-password-panel">
                          {resetEmailSent ? (
                            <Alert variant="success" className="mb-0 small">
                              <i className="fas fa-check-circle me-2"></i>
                              <strong>Email inviata!</strong> Se l'indirizzo è registrato, riceverai le istruzioni per reimpostare la password. Controlla anche la cartella spam.
                            </Alert>
                          ) : (
                            <>
                              <p className="small text-muted mb-2">
                                <i className="fas fa-info-circle me-1"></i>
                                Ti invieremo un link per reimpostare la password dell'account <strong>{formData.loginEmail}</strong>.
                              </p>
                              <div className="d-flex gap-2">
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={handleForgotPassword}
                                  disabled={resetEmailSending}
                                  type="button"
                                  data-testid="send-reset-email-btn"
                                >
                                  {resetEmailSending ? (
                                    <><Spinner animation="border" size="sm" className="me-1" />Invio...</>
                                  ) : (
                                    <><i className="fas fa-paper-plane me-1"></i>Invia email di recupero</>
                                  )}
                                </Button>
                                <Button
                                  variant="link"
                                  size="sm"
                                  className="text-muted p-0"
                                  onClick={() => setShowForgotPassword(false)}
                                  type="button"
                                >
                                  Annulla
                                </Button>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>

                    <Button
                      type="submit"
                      variant="primary"
                      size="lg"
                      className="w-100 py-3 fw-bold"
                      disabled={submitLoading}
                      data-testid="link-parent-submit-btn"
                    >
                      {submitLoading ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Collegamento in corso...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-link me-2"></i>
                          Collega Profilo e Accedi
                        </>
                      )}
                    </Button>
                  </>
                ) : (
                  <>
                    <Form.Group className="mb-3" controlId="setup-login-email">
                      <Form.Label className="fw-bold">Email di Accesso (Login) *</Form.Label>
                      <Form.Control
                        type="email"
                        name="loginEmail"
                        value={formData.loginEmail}
                        onChange={handleInputChange}
                        placeholder="email@esempio.com"
                        required
                        disabled={submitLoading}
                      />
                      <Form.Text className="text-muted">
                        Questa email sarà usata per accedere al portale.
                      </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="setup-password">
                      <Form.Label className="fw-bold">Nuova Password *</Form.Label>
                      <InputGroup>
                        <Form.Control
                          type={showPassword ? 'text' : 'password'}
                          name="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          placeholder="Almeno 6 caratteri"
                          minLength={6}
                          required
                          disabled={submitLoading}
                        />
                        <Button
                          variant="outline-secondary"
                          onClick={() => setShowPassword(!showPassword)}
                          type="button"
                        >
                          <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                        </Button>
                      </InputGroup>
                    </Form.Group>

                    {/* Forgot password inline panel */}
                    <div className="mb-3" data-testid="forgot-password-section-new">
                      {!showForgotPassword ? (
                        <div className="text-end">
                          <Button
                            variant="link"
                            className="p-0 text-decoration-none small"
                            onClick={() => { setShowForgotPassword(true); setResetEmailSent(false); }}
                            type="button"
                            data-testid="forgot-password-link-new"
                          >
                            Password dimenticata?
                          </Button>
                        </div>
                      ) : (
                        <div className="border rounded p-3 bg-light" data-testid="forgot-password-panel-new">
                          {resetEmailSent ? (
                            <Alert variant="success" className="mb-0 small">
                              <i className="fas fa-check-circle me-2"></i>
                              <strong>Email inviata!</strong> Se l'indirizzo è registrato, riceverai le istruzioni per reimpostare la password. Controlla anche la cartella spam.
                            </Alert>
                          ) : (
                            <>
                              <p className="small text-muted mb-2">
                                <i className="fas fa-info-circle me-1"></i>
                                Ti invieremo un link per reimpostare la password dell'account <strong>{formData.loginEmail || 'inserito sopra'}</strong>.
                              </p>
                              <div className="d-flex gap-2">
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={handleForgotPassword}
                                  disabled={resetEmailSending || !formData.loginEmail}
                                  type="button"
                                  data-testid="send-reset-email-btn-new"
                                >
                                  {resetEmailSending ? (
                                    <><Spinner animation="border" size="sm" className="me-1" />Invio...</>
                                  ) : (
                                    <><i className="fas fa-paper-plane me-1"></i>Invia email di recupero</>
                                  )}
                                </Button>
                                <Button
                                  variant="link"
                                  size="sm"
                                  className="text-muted p-0"
                                  onClick={() => setShowForgotPassword(false)}
                                  type="button"
                                >
                                  Annulla
                                </Button>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>

                    <Form.Group className="mb-4" controlId="setup-confirm-password">
                      <Form.Label className="fw-bold">Conferma Password *</Form.Label>
                      <Form.Control
                        type={showPassword ? 'text' : 'password'}
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        placeholder="Ripeti la password"
                        minLength={6}
                        required
                        disabled={submitLoading}
                      />
                    </Form.Group>

                    <Button
                      type="submit"
                      variant="primary"
                      size="lg"
                      className="w-100 py-3 fw-bold"
                      disabled={submitLoading}
                      data-testid="submit-setup-btn"
                    >
                      {submitLoading ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Attivazione in corso...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-check-circle me-2"></i>
                          Attiva Account e Accedi
                        </>
                      )}
                    </Button>
                  </>
                )}
              </Form>

              <div className="text-center mt-4">
                <small className="text-muted">
                  Hai già completato la registrazione?{' '}
                  <Link to="/login" className="text-decoration-none">
                    Accedi qui
                  </Link>
                </small>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Email Warning Modal */}
      <EmailWarningModal
        show={emailWarningState.warnings.length > 0}
        warnings={emailWarningState.warnings}
        onCorrect={() => setEmailWarningState({ warnings: [], onIgnore: null })}
        onIgnoreAndProceed={() => {
          const proceed = emailWarningState.onIgnore;
          setEmailWarningState({ warnings: [], onIgnore: null });
          if (proceed) proceed();
        }}
      />
    </Container>
  );
};

export default PortalSetup;
